"use client";

import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Container,
  Paper,
  CircularProgress,
  SelectChangeEvent,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Grid,
  Card,
  CardContent,
  Button,
  TablePagination,
  IconButton,
} from "@mui/material";
import ContestantList from "./components/ContestantList";
import ScoringForm from "./components/ScoringForm";
import ResultsSummary from "./components/ResultSummary";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import { apiRemote, ISubmissionTcc } from "@/api";
import { EmojiEvents } from "@mui/icons-material";
import { useNavigate } from "react-router";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import React from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const SCORING_CRITERIA = [{ id: "score", name: "Score", maxScore: process.env.MAX_SCORE || 200 }];

export default function ResultSummary() {
  const {
    submissions,
    setSubmissions,
    currentPageSubmission,
    setCurrentPageSubmission,
    totalCountSubmission,
    setTotalCountSubmission,
    topRank,
    setTopRank,
  } = useThoughtCoCreate();
  const [loadingSubmission, setLoadingSubmission] = useState<boolean>(false);
  const [pageSizeResult, setPageSizeResult] = useState(10);
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const toggleExpand = (index: any) => {
    setExpanded((prev: any) => ({ ...prev, [index]: !prev[index] }));
  };

  const loadTCCSubmission = useCallback(
    async (current_page: number, pageSize: number) => {
      setLoadingSubmission(true);
      try {
        const response = await apiRemote.getListSubmissionTcc(1, 1000);
        if (response?.data) {
          setTotalCountSubmission(response.data.total);
          setSubmissions(response.data.data);
          setTopRank(response.data.top_users);
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu:", error);
      } finally {
        setLoadingSubmission(false);
      }
    },
    []
  );

  useEffect(() => {
    loadTCCSubmission(currentPageSubmission, pageSizeResult);
  }, [loadTCCSubmission]);

  // const rankedContestants = [...topRank].sort(
  //   (a, b) => (b.score ?? 0) - (a.score ?? 0)
  // );

  const rankedContestants = [...submissions].sort(
    (a, b) => (b.score ?? 0) - (a.score ?? 0)
  );

  // const allJudges = Array.from(
  //   new Set(submissions.flatMap((submission) => submission.judges.map((j) => j.judge)))
  // );

  // Maximum possible score
  // const maxPossibleScore = SCORING_CRITERIA.reduce(
  //   (sum, criterion) => sum + criterion.maxScore,
  //   0
  // );
  const maxPossibleScore = process.env.MAX_SCORE;

  const onBack = () => {
    navigate("../");
  };

  // const handlePageChange = (event: unknown, newPage: number) => {
  //   setCurrentPageSubmission(newPage + 1);
  // };

  // const handleRowsPerPageChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   setPageSizeResult(parseInt(event.target.value, 10));
  //   setCurrentPageSubmission(1);
  // };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new(); // Tạo workbook mới

    const wsData = [
      ["Rank", "Contestant", "AI Score", "AI Score Details", "Judge", "Score"], // Tiêu đề cột
      ...rankedContestants.map((contestant, index) => {
        let aiScoreDetails = "N/A";

        // Kiểm tra nếu dữ liệu JSON hợp lệ thì xử lý
        try {
          const aiData = JSON.parse(
            // contestant.ai_score_data.replace(/\\"/g, '"')
            contestant.ai_score_data.replace(/\\+/g, "")
          );
          const scores = aiData.scores
            ? Object.entries(aiData.scores)
                .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
                .join("\n")
            : "No scores available";

          const feedback = aiData.feedback
            ? Object.entries(aiData.feedback)
                .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
                .join("\n")
            : "No feedback available";

          aiScoreDetails = `Scores:\n${scores}\n\nFeedback:\n${feedback}\n\nSummary:\n${
            aiData.summary || "N/A"
          }`;
        } catch (error) {
          console.error("Error parsing AI score data:", error);
        }

        return [
          index + 1, // Rank
          contestant.display_name, // Contestant
          contestant.ai_score, // AI Score
          aiScoreDetails, // AI Score Details (phân tích từ JSON)
          contestant.judgeInfo.display_name, // Judge
          contestant.score ?? 0, // Tổng điểm của Judge
        ];
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData); // Chuyển dữ liệu thành sheet
    ws["!cols"] = [
      { wch: 10 }, // Rank
      { wch: 20 }, // Contestant
      { wch: 10 }, // AI Score
      { wch: 40 }, // AI Score Details (tăng chiều rộng cột)
      { wch: 20 }, // Judge
      { wch: 10 }, // Score
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Results"); // Thêm sheet vào workbook

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, "scoring_results.xlsx"); // Lưu file Excel
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mb: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Scoring Results
        </Typography>

        {loadingSubmission ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
            }}
          >
            <CircularProgress />
          </Box>
        ) : totalCountSubmission === 0 ? (
          <Typography
            sx={{
              color: "rgb(102, 102, 102)",
              fontSize: "1rem",
              textAlign: "center",
            }}
          >
            No data available
          </Typography>
        ) : (
          <>
            {rankedContestants.length > 0 ? (
              <>
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  {rankedContestants.slice(0, 3).map((ranked, index) => {
                    const contestant = submissions.find(
                      (c) => c.user === ranked.user
                    );
                    if (!contestant) return null;
                    return (
                      <Grid item xs={12} sm={4} key={ranked.user}>
                        <Card
                          sx={{
                            height: "100%",
                            bgcolor:
                              index === 0
                                ? "rgba(255, 215, 0, 0.1)"
                                : "inherit",
                            border: index === 0 ? "2px solid gold" : "inherit",
                          }}
                        >
                          <CardContent sx={{ textAlign: "center" }}>
                            {index === 0 ? (
                              <EmojiEvents
                                sx={{ fontSize: 40, color: "gold", mb: 1 }}
                              />
                            ) : index === 1 ? (
                              <EmojiEvents
                                sx={{ fontSize: 40, color: "silver", mb: 1 }}
                              />
                            ) : (
                              <EmojiEvents
                                sx={{ fontSize: 40, color: "#cd7f32", mb: 1 }}
                              /> // Bronze color
                            )}
                            <Typography variant="h6" gutterBottom>
                              {index === 0
                                ? "1st Place"
                                : index === 1
                                ? "2nd Place"
                                : "3rd Place"}
                            </Typography>
                            <Typography variant="h5" component="div">
                              {ranked.display_name}
                            </Typography>
                            <Typography
                              variant="h4"
                              color="primary"
                              sx={{ mt: 2 }}
                            >
                              {ranked.score ?? 0} / {maxPossibleScore}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {Math.round(
                                ((ranked.score ?? 0) / maxPossibleScore) * 100
                              )}
                              % of maximum score
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <b>Rank</b>
                        </TableCell>
                        <TableCell>
                          <b>Contestant</b>
                        </TableCell>
                        {/* {allJudges.map((judge) => (
                          <TableCell key={judge} align="center">{judge} (Judge)</TableCell>
                        ))} */}
                        <TableCell>
                          <b>AI Score</b>
                        </TableCell>
                        <TableCell>
                          <b>AI Score Details</b>
                        </TableCell>
                        <TableCell>
                          <b>Judge</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>Score</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rankedContestants
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((contestant, index) => (
                          <>
                            <TableRow
                              key={contestant.user}
                              sx={{
                                bgcolor:
                                  index === 0
                                    ? "rgba(255, 215, 0, 0.1)"
                                    : "inherit",
                                "&:nth-of-type(odd)": {
                                  bgcolor: (theme) =>
                                    theme.palette.action.hover,
                                },
                              }}
                            >
                              <TableCell component="th" scope="row">
                                {index + 1}
                              </TableCell>
                              <TableCell>{contestant.display_name}</TableCell>
                              {/* {allJudges.map((judge) => {
                            const judgeScore = contestant.judges.find((j) => j.judge === judge)?.score ?? "N/A";
                            return <TableCell key={judge} align="center">{judgeScore}</TableCell>;
                          })} */}
                              <TableCell>{contestant.ai_score}</TableCell>
                              <TableCell>
                                <IconButton onClick={() => toggleExpand(index)}>
                                  {expanded[index] ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                {contestant.judgeInfo?.display_name || "N/A"}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontWeight: "bold" }}
                              >
                                {contestant.score ?? 0} / {maxPossibleScore}
                              </TableCell>
                            </TableRow>
                            {expanded[index] && (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography variant="subtitle1">
                                    <b>AI Score Breakdown:</b>
                                  </Typography>
                                  <ul>
                                    {Object.entries(
                                      JSON.parse(
                                        contestant.ai_score_data.replace(
                                          /\\+/g,
                                          ""
                                        )
                                      ).scores
                                    ).map(([key, value]) => (
                                      <li key={key}>
                                        <b>{key.replace(/_/g, " ")}:</b> {value}
                                      </li>
                                    ))}
                                  </ul>
                                  <Typography variant="subtitle1">
                                    <b>Feedback:</b>
                                  </Typography>
                                  <ul>
                                    {Object.entries(
                                      JSON.parse(
                                        contestant.ai_score_data.replace(
                                          /\\+/g,
                                          ""
                                        )
                                      ).feedback
                                    ).map(([key, value]) => (
                                      <li key={key}>
                                        <b>{key.replace(/_/g, " ")}:</b> {value}
                                      </li>
                                    ))}
                                  </ul>
                                  <Typography variant="body1">
                                    <b>Summary:</b>{" "}
                                    {
                                      JSON.parse(
                                        contestant.ai_score_data.replace(
                                          /\\+/g,
                                          ""
                                        )
                                      ).summary
                                    }
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={exportToExcel}
                    startIcon={<EmojiEvents />}
                  >
                    Export to Excel
                  </Button>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rankedContestants.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </>
            ) : (
              <Typography variant="body1" sx={{ my: 4 }}>
                No scores have been submitted yet.
              </Typography>
            )}
          </>
        )}

        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      </Paper>
    </Container>
  );
}
